import { KeyboardEvent } from "react";
import { isValid } from "date-fns";
import type { TFunction } from "i18next";
import qs, { ParsedQs } from "qs";

import { APIFilterOp } from "shared/api/utils";
import {
  DATE_WITH_TIME_FORMAT,
  GLOBAL_PAGE_KEY_PREFIX,
  MAX_FILTER_ID,
  MIN_FILTER_ID,
  SHORT_DATE_FORMAT,
  TABLE_STATE_LOCAL_STORAGE_PREFIX,
} from "shared/constants";
import { formatDate } from "shared/locale/format";
import { DataType, SortBy } from "shared/types";
import {
  areArraysEqual,
  cleanString,
  cloneObject,
  randomID,
} from "shared/utils";

import { AssociatedVehiclesState } from "pages/shared/analytics/associatedVehicles/types";

import { SelectedChartOptions } from "features/ui/charts/Actions/types";
import { SchemaEntry } from "features/ui/MaterialTable";
import { SelectOption } from "features/ui/Select";

import { useQuery } from "services/hooks";

import {
  ANY_ALL_INDEX,
  API_FILTER_TO_OPERATOR,
  DEFAULT_OPERATOR_PER_TYPE,
  getOperatorToLabel,
  getOperatorToLabelOverrideByFilterType,
  getOperatorToSign,
  getOperatorToWords,
  OPERATORS_MAP,
} from "./constants";
import { DEFAULT_FILTER_BUILDER_STATE } from "./FilterBuilder/constants";
import {
  FilterGroupState,
  FilterRowState,
  FilterRowStateNotNull,
} from "./FilterBuilder/types";
import {
  filterBuilderQueryToFilterBuilderState,
  getFiltersQuery,
  isDefaultAdvancedFilterState,
  isFilterBuilderStateValid,
  oldFilterStateToNew,
  removeAttributesFromFilterGroupState,
  updateOrAddRowFilterGroupState,
  withoutIncompleteRanges,
} from "./FilterBuilder/utils";
import {
  assertIsPageChartSettingsState,
  FilterDiffChange,
  FilterDiffResult,
  FilterEntry,
  FilterOperator,
  FilterOverviewFormat,
  FilterSortState,
  FilterState,
  FilterType,
  FilterValue,
  InitialDateValues,
  OccursFilterState,
  PageChartSettingsState,
  QueryKeys,
  SingleFilterState,
  UseInitialStateValuesAndKeysReturn,
} from "./types";

const DEFAULT_FILTER_KEY_MAP_VALUES = {};

const isDateField = (type?: DataType) =>
  type && [DataType.DATE, DataType.DATE_UTC].includes(type);

export const isDateTimeField = (type?: DataType) =>
  !!type &&
  [
    DataType.DATE_WITH_TIME,
    DataType.DATE_WITH_TIME_NO_TZ,
    DataType.DATE_WITH_TIME_UTC,
  ].includes(type);

export const isDateOrDateTimeField = (type?: DataType) =>
  isDateField(type) || isDateTimeField(type);

export const isLastDateFilter = (values: string[], operator: FilterOperator) =>
  operator === FilterOperator.IN_LAST &&
  values.length === 1 &&
  isValueInLastXFormat(values[0]);

export const getFilterKeyMapValues = (schema: SchemaEntry[]) =>
  schema.reduce((obj, { filter }) => {
    if (!filter) return obj;

    Object.assign(obj, { [filter.fieldName]: filter.label });

    return obj;
  }, DEFAULT_FILTER_KEY_MAP_VALUES);

// Identifies a filter row within the flat filter summary. A row nested inside
// an EXISTS/relation block is qualified by its relation's parent accessor, so
// the same child attribute on two different relations (e.g. `defects.line` vs
// `lineExits.line`) stays distinct and can be hidden independently.
export const getFilterSummaryFieldKey = (
  attribute: string,
  relationAccessor?: string
): string =>
  relationAccessor ? `${relationAccessor}.${attribute}` : attribute;

// Two date filters can share a field, so a date row's summary label is keyed by
// its value too. Values are cut to the date: the same dropdown option yields a
// date or a date-time row depending on the field's data type.
export const getDateSummaryLabelKey = (
  fieldKey: string,
  { operator, values }: Pick<FilterRowState, "operator" | "values">
): string =>
  `${fieldKey}|${operator}|${(values ?? []).map((v) => v.slice(0, 10)).join(",")}`;

export const operatorToSelectOption = (
  t: TFunction,
  type: FilterType,
  op: FilterOperator
): SelectOption<FilterOperator> => {
  let value = getOperatorToLabel(t)[op] || cleanString(op);

  const typeOverride = getOperatorToLabelOverrideByFilterType(t)[type]?.[op];
  if (typeOverride !== undefined) {
    value = typeOverride;
  }

  return { id: op, value };
};

export interface OperatorsToSelectOptionsProps {
  type: FilterType;
  disableSelectFilters?: boolean;
  disableContainsFilters?: boolean;
  disableStartsWithFilters?: boolean;
  disableIsEmptyFilters?: boolean;
  disableIsNotFilteredFilters?: boolean;
  whitelistedFilterOperators?: FilterOperator[];
}

const filterOutOperators = (
  {
    disableSelectFilters,
    disableContainsFilters,
    disableStartsWithFilters,
    disableIsEmptyFilters,
    disableIsNotFilteredFilters,
    whitelistedFilterOperators,
  }: OperatorsToSelectOptionsProps,
  operator: FilterOperator
) => {
  if (
    whitelistedFilterOperators &&
    !whitelistedFilterOperators.includes(operator)
  ) {
    return false;
  }

  if (
    disableSelectFilters &&
    [FilterOperator.IN, FilterOperator.NOT_IN].includes(operator)
  ) {
    return false;
  }

  if (
    disableContainsFilters &&
    [FilterOperator.CONTAINS, FilterOperator.NOT_CONTAINS].includes(operator)
  ) {
    return false;
  }

  if (
    disableStartsWithFilters &&
    [FilterOperator.STARTS_WITH, FilterOperator.NOT_STARTS_WITH].includes(
      operator
    )
  ) {
    return false;
  }

  if (
    disableIsEmptyFilters &&
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY].includes(operator)
  ) {
    return false;
  }

  if (
    disableIsNotFilteredFilters &&
    [FilterOperator.NOT_FILTERED].includes(operator)
  ) {
    return false;
  }

  return true;
};

export const getOperatorsSelectOptions = (
  t: TFunction,
  props: OperatorsToSelectOptionsProps
): SelectOption<FilterOperator>[][] => {
  const { type } = props;

  return OPERATORS_MAP[type].map((opList) =>
    opList
      .filter((op) => filterOutOperators(props, op))
      .map((op) => operatorToSelectOption(t, type, op))
  );
};

export const getDefaultOperatorSelectOption = (
  t: TFunction,
  props: OperatorsToSelectOptionsProps
) => {
  const { type } = props;
  const allowedFilters = OPERATORS_MAP[type].map((opList) =>
    opList.filter((op) => filterOutOperators(props, op))
  );

  const defaultOperator =
    allowedFilters.flat()[0] || DEFAULT_OPERATOR_PER_TYPE[type];

  return operatorToSelectOption(t, type, defaultOperator);
};

export const getFormattedFilter = (
  t: TFunction,
  { fieldName, operator, fieldValue }: FilterEntry,
  format: FilterOverviewFormat,
  dataType: DataType
) => {
  const formattedFilter =
    format === "badge"
      ? getFormattedFilterForBadge
      : getFormattedFilterForLabel;

  return formattedFilter(t, fieldName, operator, fieldValue, dataType);
};

const getFormattedFilterForBadge = (
  t: TFunction,
  fieldName: string,
  operator: FilterOperator,
  fieldValue: FilterValue,
  dataType: DataType
) => {
  const formattedOperator = getOperatorToSign(t)[operator];
  let formattedFieldValue = fieldValue;

  if (
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY].includes(operator)
  ) {
    formattedFieldValue = "";
  }

  if (typeof fieldValue === "string" && isDateOrDateTimeField(dataType)) {
    // if we need to format the date more, we can do it here..
    // - dates dont have IN operator, so we dont need to worry about that
    if (operator === FilterOperator.BETWEEN) {
      formattedFieldValue = `${fieldValue}`;
    } else if (operator === FilterOperator.IN_LAST) {
    } else {
      if (isDateTimeField(dataType)) {
        const hasTimeInString = fieldValue.includes("T");
        formattedFieldValue = hasTimeInString
          ? formatDate(fieldValue, DATE_WITH_TIME_FORMAT)
          : formatDate(fieldValue, SHORT_DATE_FORMAT);
      } else {
        formattedFieldValue = formatDate(fieldValue, SHORT_DATE_FORMAT, true);
      }
    }
  }

  return {
    formattedFieldName: fieldName,
    formattedOperator,
    formattedFieldValue,
  };
};

const getFormattedFilterForLabel = (
  t: TFunction,
  fieldName: string,
  operator: FilterOperator,
  fieldValue: FilterValue,
  dataType: DataType
) => {
  let formattedOperator = getOperatorToWords(t)[operator];
  let formattedFieldValue = fieldValue;

  if (
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY].includes(operator)
  ) {
    formattedFieldValue = "";
    // Use the full sign phrase ("is empty" / "is not empty") so the label is a
    // single translatable string rather than a runtime concatenation.
    formattedOperator = getOperatorToSign(t)[operator];
  }

  if (typeof fieldValue === "string" && isDateOrDateTimeField(dataType)) {
    // if we need to format the date more, we can do it here..
    // - dates dont have IN operator, so we dont need to worry about that
    if (operator === FilterOperator.BETWEEN) {
      formattedFieldValue = `${fieldValue}`;
    } else if (operator === FilterOperator.IN_LAST) {
    } else if (isDateTimeField(dataType)) {
      formattedFieldValue = formatDate(fieldValue, DATE_WITH_TIME_FORMAT);
    } else {
      formattedFieldValue = formatDate(fieldValue, SHORT_DATE_FORMAT, true);
    }
  }

  return {
    formattedFieldName: fieldName,
    formattedOperator,
    formattedFieldValue,
  };
};

/**
 * Last X days/weeks/months/years
 */
// will match 12m, 12d, 12w, 12y (or any other number) but not if spaces or anything else is present .. has to start with a number and end with a letter..
const regexToMatchIsLastFormat = /^\d{1,}(?:d|w|m|y)$/;

export type DateOption = "d" | "w" | "m" | "y";

export const getDateUnitOptions = (
  t: TFunction
): SelectOption<DateOption>[] => [
  { id: "d", value: t("date_units.d") },
  { id: "w", value: t("date_units.w") },
  { id: "m", value: t("date_units.m") },
  { id: "y", value: t("date_units.y") },
];

// index of the default unit ("months") in getDateUnitOptions
const DEFAULT_DATE_UNIT_INDEX = 2;
export const DEFAULT_LAST_X_VALUE = 36;

export const getDefaultLastXValue = (initialValues?: InitialDateValues) => {
  if (
    !initialValues ||
    !initialValues[0] ||
    !isValueInLastXFormat(initialValues[0])
  ) {
    return DEFAULT_LAST_X_VALUE.toString();
  }

  return initialValues[0].replace(/\D/g, "");
};

export const getDefaultDateUnitOption = (
  t: TFunction,
  initialValues?: InitialDateValues
) => {
  const options = getDateUnitOptions(t);

  if (
    !initialValues ||
    !initialValues[0] ||
    !isValueInLastXFormat(initialValues[0])
  ) {
    return options[DEFAULT_DATE_UNIT_INDEX];
  }

  const unitValue = initialValues[0].slice(-1);

  return options.find(({ id }) => id === unitValue)!;
};

export const isValueInLastXFormat = (value: string | null) =>
  value && regexToMatchIsLastFormat.test(value);

export const onlyAllowPositiveIntegersOnKeyDown = (event: KeyboardEvent) => {
  if (
    ((event.metaKey || event.ctrlKey) && event.code === "KeyA") ||
    [
      "Delete",
      "Backspace",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
    ].includes(event.code) ||
    event.code.startsWith("Digit")
  ) {
    return true;
  }
};

export const getFilterType = (type: string | undefined): FilterType => {
  if (type && (type.includes("int") || type.includes("double"))) {
    return FilterType.NUMBER;
  }

  if (type && type.includes("date")) {
    return FilterType.DATE;
  }

  return FilterType.STRING;
};

// we only do it for the first depth, because pending update/removal is not supported for nested filters in the app
export const getUpdatedDeletedAttributes = (
  objA?: FilterGroupState,
  objB?: FilterGroupState
): { updated: string[]; deleted: string[] } => {
  const updated: string[] = [];
  const deleted: string[] = [];

  const objBAttributes = objB?.children
    ?.filter((child) => child.type === "row")
    ?.map((child) => (child as FilterRowState).attribute);

  // Check for updated and removed keys
  objA?.children
    ?.filter((child) => child.type === "row")
    .forEach((rowOrGroup) => {
      const { attribute, operator, values } = rowOrGroup as FilterRowState;

      if (!objBAttributes?.includes(attribute)) {
        attribute && deleted.push(attribute);
      } else {
        // build two SingleFilterState objects to compare
        const objAFilter: SingleFilterState = {
          operator: operator || FilterOperator.NOT_FILTERED,
          values: values || [],
        };
        const objBFilterRowState = objB?.children.find(
          (child) =>
            child.type === "row" &&
            (child as FilterRowState).attribute === attribute
        ) as FilterRowState | undefined;

        const objBFilter: SingleFilterState = {
          operator: objBFilterRowState?.operator || FilterOperator.NOT_FILTERED,
          values: objBFilterRowState?.values || [],
        };

        if (!singleFiltersMatch(objAFilter, objBFilter)) {
          attribute && updated.push(attribute);
        }
      }
    });

  // Check for keys added in objB
  objB?.children
    ?.filter(({ type }) => type === "row")
    .forEach((rowOrGroup) => {
      const currentAttribute = (rowOrGroup as FilterRowState).attribute;
      if (
        !objA?.children.some(
          (child) =>
            child.type === "row" && child.attribute === currentAttribute
        )
      ) {
        currentAttribute && updated.push(currentAttribute);
      }
    });

  return { updated, deleted };
};

export const singleFiltersMatch = (
  filter1?: SingleFilterState,
  filter2?: SingleFilterState
): boolean => {
  if (filter1 === filter2) return true;

  if (!filter1 || !filter2) return false;

  return (
    Object.keys(filter1).length === Object.keys(filter2).length &&
    Object.keys(filter1).every((key) => Object.keys(filter2).includes(key)) &&
    filter1.operator === filter2.operator &&
    areArraysEqual(filter1.values, filter2.values) &&
    // naive
    JSON.stringify(filter1.extra) === JSON.stringify(filter2.extra)
  );
};

const compareFilterRows = (
  oldRow: FilterRowState,
  newRow: FilterRowState
): FilterDiffChange => ({
  attribute: oldRow.attribute !== newRow.attribute,
  operator: oldRow.operator !== newRow.operator,
  values: !areArraysEqual(oldRow.values || [], newRow.values || []),
});

export const diffFilterGroupStates = (
  oldState?: FilterGroupState,
  newState?: FilterGroupState
): FilterDiffResult => {
  const result: FilterDiffResult = {
    deletionChanges: {},
    additionChanges: {},
  };

  const processLevel = (
    oldGroup?: FilterGroupState,
    newGroup?: FilterGroupState,
    level: number = 0,
    groupIndex: number = 0
  ) => {
    if (!oldGroup && !newGroup) return;

    // initialize the level in our result objects if it doesn't exist
    if (!result.deletionChanges[level]) result.deletionChanges[level] = {};

    if (!result.additionChanges[level]) result.additionChanges[level] = {};

    // initialize the group index in our result objects if it doesn't exist
    if (!result.deletionChanges[level][groupIndex])
      result.deletionChanges[level][groupIndex] = {};

    if (!result.additionChanges[level][groupIndex])
      result.additionChanges[level][groupIndex] = {};

    // markAll and anyAll are saved as row with index -1 in its group
    // check if one of the groups is undefined
    if (!oldGroup && newGroup) {
      result.additionChanges[level][groupIndex][ANY_ALL_INDEX] = {
        markAll: true,
      };
    } else if (oldGroup && !newGroup) {
      result.deletionChanges[level][groupIndex][ANY_ALL_INDEX] = {
        markAll: true,
      };
    }

    // compare AnyAll at group level
    if (oldGroup?.anyAll !== newGroup?.anyAll) {
      if (oldGroup) {
        result.deletionChanges[level][groupIndex][ANY_ALL_INDEX] = {
          ...result.deletionChanges[level][groupIndex][ANY_ALL_INDEX],
          anyAll: true,
        };
      }

      if (newGroup) {
        result.additionChanges[level][groupIndex][ANY_ALL_INDEX] = {
          ...result.additionChanges[level][groupIndex][ANY_ALL_INDEX],
          anyAll: true,
        };
      }
    }

    const oldRows =
      (oldGroup?.children.filter(
        (child) => child.type === "row"
      ) as FilterRowState[]) || [];
    const newRows =
      (newGroup?.children.filter(
        (child) => child.type === "row"
      ) as FilterRowState[]) || [];

    // we keep track of the index for rows on each level separately from groups
    // track changes in rows
    const maxRows = Math.max(oldRows.length, newRows.length);

    for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
      const oldRow = oldRows[rowIndex];
      const newRow = newRows[rowIndex];

      if (!oldRow && newRow) {
        // row was added
        result.additionChanges[level][groupIndex][rowIndex] = {
          attribute: true,
          operator: true,
          values: true,
        };
      } else if (oldRow && !newRow) {
        // row was deleted
        result.deletionChanges[level][groupIndex][rowIndex] = {
          attribute: true,
          operator: true,
          values: true,
        };
      } else if (oldRow && newRow) {
        // both rows exist, check if they're different
        if (
          oldRow.attribute !== newRow.attribute ||
          oldRow.operator !== newRow.operator ||
          !areArraysEqual(oldRow.values || [], newRow.values || [])
        ) {
          // row was modified
          result.deletionChanges[level][groupIndex][rowIndex] =
            compareFilterRows(oldRow, newRow);
          result.additionChanges[level][groupIndex][rowIndex] =
            compareFilterRows(newRow, oldRow);
        }
      }
    }

    // process nested groups
    const oldGroups =
      (oldGroup?.children.filter(
        (child) => child.type === "group"
      ) as FilterGroupState[]) || [];
    const newGroups =
      (newGroup?.children.filter(
        (child) => child.type === "group"
      ) as FilterGroupState[]) || [];

    const maxGroups = Math.max(oldGroups.length, newGroups.length);

    for (let i = 0; i < maxGroups; i++) {
      processLevel(oldGroups[i], newGroups[i], level + 1, i);
    }
  };

  processLevel(oldState, newState, 0, 0);

  return result;
};

// Adjust values so that they are compatible with the selected operator
// (e.g. if operator is "is empty", values should be ["null"])
export const prepareFilterValuesForAPI = (
  currentFilterValues: string[],
  operator: FilterOperator
): string[] => {
  let values: string[] = [...currentFilterValues];

  if (FilterOperator.NOT_FILTERED === operator) {
    values = [];
  }

  if (
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY].includes(operator)
  ) {
    values = ["null"];
  }

  if (
    [FilterOperator.HAS_NONE, FilterOperator.HAS_ONE_OR_MORE].includes(operator)
  ) {
    values = ["0"];
  }

  if ([FilterOperator.IS_TRUE, FilterOperator.IS_FALSE].includes(operator)) {
    values = [operator === FilterOperator.IS_TRUE ? "true" : "false"];
  }

  // The two slots are the two bounds, so an empty one keeps its place: dropping it would move an upper bound typed
  // first into the lower box. A range with neither bound is no value at all.
  if (operator === FilterOperator.BETWEEN) {
    const [lower = "", upper = ""] = values;

    return lower === "" && upper === "" ? [] : [lower, upper];
  }

  if (
    [
      FilterOperator.GREATER_OR_EQUAL,
      FilterOperator.GREATER_THAN,
      FilterOperator.LESS_OR_EQUAL,
      FilterOperator.LESS_THAN,
      FilterOperator.IN_LAST,
    ].includes(operator) &&
    values.length > 0
  ) {
    values = [values[0]];
  }

  return values.filter((v) => v !== "");
};

export const areFilterValuesComplete = (
  operator: FilterOperator,
  values: string[]
): boolean =>
  operator === FilterOperator.BETWEEN
    ? values.length === 2 && values.every((value) => value !== "")
    : values.length > 0;

export const resetFilters = (
  filters: FilterGroupState | undefined,
  accessors: string[],
  removedKeysArray: string[] = [],
  filterToResetToIfRemoved: FilterGroupState | undefined
): FilterGroupState => {
  if (!filters) return DEFAULT_FILTER_BUILDER_STATE;

  let newFilters: FilterGroupState = cloneObject(filters);

  for (const accessor of accessors) {
    if (!removedKeysArray.includes(accessor)) {
      const filterWithoutAccessor = removeAttributesFromFilterGroupState(
        newFilters,
        [accessor]
      );

      if (!filterWithoutAccessor) {
        newFilters = DEFAULT_FILTER_BUILDER_STATE;
      } else {
        Object.assign(
          newFilters,
          removeAttributesFromFilterGroupState(newFilters, [accessor])
        );
      }
    }

    if (removedKeysArray && removedKeysArray.includes(accessor)) {
      const accessorRow =
        filterToResetToIfRemoved &&
        (filterToResetToIfRemoved.children.find(
          (child) => child.type === "row" && child.attribute === accessor
        ) as FilterRowStateNotNull | undefined);

      if (accessorRow) {
        newFilters = updateOrAddRowFilterGroupState(newFilters, {
          ...accessorRow,
          id: `row-${randomID()}`,
        });
      }
    }
  }

  return newFilters;
};

export const getMinMaxSelectOptions = (
  t: TFunction,
  filterType?: FilterType
): SelectOption[] | undefined => {
  if (filterType === "date" || filterType === "number") {
    return [
      { id: MAX_FILTER_ID, value: t(`min_max.${filterType}.${MAX_FILTER_ID}`) },
      { id: MIN_FILTER_ID, value: t(`min_max.${filterType}.${MIN_FILTER_ID}`) },
    ];
  }

  return;
};

export const hasSomeFiltersApplied = (filter: FilterState): boolean => {
  if (!isFilterStateCorrect(filter)) return false;

  return Object.values(filter).some(
    ({ operator, values }) =>
      operator !== FilterOperator.NOT_FILTERED &&
      areFilterValuesComplete(operator, values)
  );
};

// Judged on the filters the page sends, so a range still being picked neither counts nor hides the rest.
export const hasSomeFiltersAppliedFilterGroupState = (
  filter: FilterGroupState
): boolean => {
  const settled = withoutIncompleteRanges(filter);

  if (!settled.children || settled.children.length === 0) return false;

  return isFilterBuilderStateValid(settled);
};

/**
 * Returns the number of rows that have filters applied in the filter group state.
 * It counts inside nested groups as well.
 */
export const getFilterStateCount = (filter: FilterGroupState): number => {
  const settled = withoutIncompleteRanges(filter);

  if (
    isDefaultAdvancedFilterState(settled) ||
    !isFilterBuilderStateValid(settled)
  ) {
    return 0;
  }

  const countGroupRowsApplied = (filterGroup: FilterGroupState): number =>
    filterGroup.children.reduce((acc, child) => {
      if (child.type === "group") {
        return acc + countGroupRowsApplied(child);
      }

      const { operator, values } = child as FilterRowState;

      return operator &&
        operator !== FilterOperator.NOT_FILTERED &&
        values &&
        areFilterValuesComplete(operator, values)
        ? acc + 1
        : acc;
    }, 0);

  return countGroupRowsApplied(settled);
};

export const getAttributesFromFilter = (filter?: FilterState): string[] =>
  filter ? Object.keys(filter) : [];

export const addToFilterState = (
  target: FilterState,
  attribute: string,
  stateToAdd: SingleFilterState
) => {
  Object.assign(target, { [attribute]: stateToAdd });
};

export const isFilterStateCorrect = (
  filterState: any
): filterState is FilterState => {
  if (!filterState) return false;

  const keys = getAttributesFromFilter(filterState as FilterState);

  if (!keys.length) {
    return true;
  }

  return keys.every((key) => {
    const values = (filterState as FilterState)[key]?.values;
    const operator = (filterState as FilterState)[key]?.operator;

    if (!operator || !Array.isArray(values)) {
      return false;
    }

    return true;
  });
};

const getChartSettingsFromQuery = (
  query: qs.ParsedQs,
  chartSettingsKey: string
): PageChartSettingsState | undefined => {
  const chartSettingsParam = query[chartSettingsKey];
  if (!chartSettingsParam) {
    return undefined;
  }

  try {
    const chartSettings: PageChartSettingsState = JSON.parse(
      String(chartSettingsParam)
    );
    assertIsPageChartSettingsState(chartSettings);

    return chartSettings;
  } catch (error) {
    console.error(
      "Invalid chartSettings in query string",
      error,
      chartSettingsParam
    );

    return undefined;
  }
};

const getRelatedSignalEventsFilterFromQuery = (
  query: qs.ParsedQs,
  relatedSignalEventsFilterKey: string
): OccursFilterState | undefined => {
  const relatedSignalEventsFilterParam = query[relatedSignalEventsFilterKey];
  if (!relatedSignalEventsFilterParam) {
    return undefined;
  }

  try {
    return JSON.parse(String(relatedSignalEventsFilterParam));
  } catch (error) {
    console.error(
      "Invalid relatedSignalEventsFilter in query string",
      error,
      relatedSignalEventsFilterParam
    );

    return undefined;
  }
};

const getAssociatedVehiclesStateFromQuery = (
  query: qs.ParsedQs,
  associatedVehiclesStateKey: string
): AssociatedVehiclesState | undefined => {
  const associatedVehiclesState = query[associatedVehiclesStateKey];
  if (!associatedVehiclesState) {
    return undefined;
  }

  try {
    return JSON.parse(String(associatedVehiclesState));
  } catch (error) {
    console.error(
      "Invalid associatedVehiclesState in query string",
      error,
      associatedVehiclesState
    );

    return undefined;
  }
};

const getQueryKeys = (pageKeyWithVersion: string): QueryKeys => ({
  filtersKey: getURIKey("filters", pageKeyWithVersion),
  quickFiltersKey: getURIKey("quickFilters", pageKeyWithVersion),
  sortKey: getURIKey("sort", pageKeyWithVersion),
  columnsKey: getURIKey("columns", pageKeyWithVersion),
  chartSettingsKey: getURIKey("chartSettings", pageKeyWithVersion),
  isAdvancedEditorKey: getURIKey("isAdvancedEditor", pageKeyWithVersion),
  relatedSignalEventsFilterKey: getURIKey(
    "relatedSignalEventsFilter",
    pageKeyWithVersion
  ),
  associatedVehiclesStateKey: getURIKey(
    "associatedVehiclesState",
    pageKeyWithVersion
  ),
});

export const useInitialStateValuesAndKeys = (
  pageKeyWithVersion: string,
  disableUsingQuery: boolean,
  disableUsingLocalStorage: boolean,
  defaultFilterValues?: FilterGroupState
): UseInitialStateValuesAndKeysReturn => {
  const query = useQuery();

  // Note that only URI keys are modified, localStorage keys stay the same ("filters", "sort", "columns").
  const {
    filtersKey,
    quickFiltersKey,
    sortKey,
    columnsKey,
    chartSettingsKey,
    relatedSignalEventsFilterKey,
    isAdvancedEditorKey,
    associatedVehiclesStateKey,
  } = getQueryKeys(pageKeyWithVersion);

  const queryFilters = query[filtersKey]
    ? filterBuilderQueryToFilterBuilderState(query[filtersKey] as string)
    : undefined;

  const queryQuickFilters = query[quickFiltersKey]
    ? (JSON.parse(
        (query[quickFiltersKey] as string) ?? "[]"
      ) as FilterRowState[])
    : queryFilters
      ? filtersToQuickFilters(queryFilters)
      : undefined;

  const querySort = query[sortKey] as SortBy;
  const queryColumns = query[columnsKey] as string[];
  const queryChartSettings = getChartSettingsFromQuery(query, chartSettingsKey);
  const queryRelatedSignalEventsFilter = getRelatedSignalEventsFilterFromQuery(
    query,
    relatedSignalEventsFilterKey
  );
  const queryAssociatedVehiclesState = getAssociatedVehiclesStateFromQuery(
    query,
    associatedVehiclesStateKey
  );

  let initialValues: FilterSortState = {};
  // booleans are saved to query as strings "false" and "true"
  const isAdvancedEditor = query[isAdvancedEditorKey] === "true";

  // If any of the state parameters are present in the query, we assume the user
  // wants to see a specific configuration rather than their current one from local storage.
  if (
    // We still want to check if query for filters was defined as filterBuilderQueryToFilterBuilderState converts empty
    // string to undefined and it slips through cracks. If empty string is in query, we want to have a clean slate and not load
    // existing data from local storage
    !disableUsingQuery &&
    (query[filtersKey] !== undefined ||
      queryFilters !== undefined ||
      querySort !== undefined ||
      queryColumns !== undefined ||
      queryChartSettings !== undefined ||
      queryQuickFilters !== undefined ||
      queryRelatedSignalEventsFilter !== undefined ||
      queryAssociatedVehiclesState !== undefined)
  ) {
    initialValues = {
      filters: queryFilters,
      quickFilters: queryQuickFilters,
      sort: querySort,
      columns: queryColumns,
      chartSettings: queryChartSettings,
      relatedSignalEventsFilter: queryRelatedSignalEventsFilter,
      isAdvancedEditor,
      associatedVehiclesState: queryAssociatedVehiclesState,
    };
  } else if (!disableUsingLocalStorage) {
    initialValues = getStateFromLocalStorage(
      pageKeyWithVersion,
      defaultFilterValues
    );
  }

  // When disableUsingQuery is true && defaultFilterValues is provided,
  // we want to set the initial filters to be defaultFilterValues.
  // - Ie. in FilterWizard, we want to use the defaultFilterValues if they are provided
  //   and we don't rely on the query params there ..
  if (disableUsingQuery && defaultFilterValues) {
    initialValues.filters = defaultFilterValues;
    initialValues.quickFilters = filtersToQuickFilters(defaultFilterValues);
  }

  return {
    initialValues,
    queryKeys: {
      filtersKey,
      quickFiltersKey,
      sortKey,
      columnsKey,
      chartSettingsKey,
      relatedSignalEventsFilterKey,
      isAdvancedEditorKey,
      associatedVehiclesStateKey,
    },
  };
};

export const mergeChartOptions = (
  target: SelectedChartOptions[],
  source: SelectedChartOptions[]
): SelectedChartOptions[] => {
  const mergedMap = new Map(
    target.map((item) => {
      const clonedItem = cloneObject(item);

      return [clonedItem.id, clonedItem];
    })
  );

  source.forEach((sourceItem) => {
    mergedMap.set(sourceItem.id, sourceItem);
  });

  return Array.from(mergedMap.values());
};

export const injectPageChartSettings = (
  target: PageChartSettingsState | undefined,
  source: PageChartSettingsState | undefined
): PageChartSettingsState | undefined => {
  if (!source || !target) {
    return source ?? target;
  }

  const result = cloneObject(target);

  Object.keys(source).forEach((tabKey) => {
    const tabSettings = source[tabKey];

    if (!result[tabKey]) {
      result[tabKey] = tabSettings;
    } else {
      Object.keys(tabSettings).forEach((chartKey) => {
        const chartSettings = tabSettings[chartKey];

        if (!result[tabKey][chartKey]) {
          result[tabKey][chartKey] = chartSettings;
        } else {
          result[tabKey][chartKey] = mergeChartOptions(
            result[tabKey][chartKey],
            chartSettings
          );
        }
      });
    }
  });

  return result;
};

export const getPageKeyWithVersion = (pageKey: string) =>
  `${GLOBAL_PAGE_KEY_PREFIX}.${pageKey}`;

export const getFiltersKey = (pageKey: string) =>
  getURIKey("filters", getPageKeyWithVersion(pageKey));

export const getChartSettingsKey = (pageKey: string) =>
  getURIKey("chartSettings", getPageKeyWithVersion(pageKey));

export const getQuickFiltersKey = (pageKey: string) =>
  getURIKey("quickFilters", getPageKeyWithVersion(pageKey));

export const getIsAdvancedEditorKey = (pageKey: string) =>
  getURIKey("isAdvancedEditor", getPageKeyWithVersion(pageKey));

const getURIKey = (stateAttribute: keyof FilterSortState, pageKey: string) =>
  `${stateAttribute}_${pageKey}`;

export const getStateFromLocalStorage = (
  pageKey: string,
  defaultFilterValues?: FilterGroupState,
  defaultQuickFilters?: FilterRowState[]
): FilterSortState => {
  let filterSortStateLocalStorage: FilterSortState;
  try {
    filterSortStateLocalStorage = JSON.parse(
      localStorage.getItem(`${TABLE_STATE_LOCAL_STORAGE_PREFIX}.${pageKey}`) ??
        "{}"
    );
  } catch (parseError) {
    console.error(
      parseError,
      localStorage.getItem(`${TABLE_STATE_LOCAL_STORAGE_PREFIX}.${pageKey}`)
    );
    filterSortStateLocalStorage = {};
  }

  const filtersFromLocalStorage = oldFilterStateToNew(
    filterSortStateLocalStorage.filters
  );

  const isAdvancedEditorFromLocalStorage =
    filterSortStateLocalStorage.isAdvancedEditor;

  const quickFiltersFromLocalStorage = filterSortStateLocalStorage.quickFilters;

  const sortFromLocalStorage = filterSortStateLocalStorage.sort;

  const columnsFromLocalStorage = filterSortStateLocalStorage.columns;

  const chartSettingsFromLocalStorage =
    filterSortStateLocalStorage.chartSettings;

  const relatedSignalEventFilterFromLocalStorage =
    filterSortStateLocalStorage.relatedSignalEventsFilter;

  const associatedVehiclesStateFromLocalStorage =
    filterSortStateLocalStorage.associatedVehiclesState;

  const localStorageFilterSortState: FilterSortState = {};

  if (quickFiltersFromLocalStorage !== undefined) {
    localStorageFilterSortState.quickFilters = quickFiltersFromLocalStorage;
  } else if (defaultQuickFilters) {
    localStorageFilterSortState.quickFilters = cloneObject(defaultQuickFilters);
  }

  if (isAdvancedEditorFromLocalStorage !== undefined) {
    localStorageFilterSortState.isAdvancedEditor =
      isAdvancedEditorFromLocalStorage;
  }

  if (sortFromLocalStorage !== undefined) {
    localStorageFilterSortState.sort = sortFromLocalStorage;
  }

  if (filtersFromLocalStorage !== undefined) {
    localStorageFilterSortState.filters = filtersFromLocalStorage;
  } else if (
    defaultFilterValues &&
    isFilterBuilderStateValid(defaultFilterValues)
  ) {
    localStorageFilterSortState.filters = cloneObject(defaultFilterValues);
  }

  if (columnsFromLocalStorage !== undefined) {
    localStorageFilterSortState.columns = columnsFromLocalStorage;
  }

  if (chartSettingsFromLocalStorage !== undefined) {
    localStorageFilterSortState.chartSettings = chartSettingsFromLocalStorage;
  }

  if (relatedSignalEventFilterFromLocalStorage !== undefined) {
    localStorageFilterSortState.relatedSignalEventsFilter =
      relatedSignalEventFilterFromLocalStorage;
  }

  if (associatedVehiclesStateFromLocalStorage !== undefined) {
    localStorageFilterSortState.associatedVehiclesState =
      associatedVehiclesStateFromLocalStorage;
  }

  return localStorageFilterSortState;
};

/**
 * We are modifying the URI params to use our own keys instead of the ones in FilterSortState.
 * - We also change FilterState to query string format so we can use it in the URI
 * - And we set the values to empty string if they are empty objects.
 */
const modifyURIParams = (
  {
    filters,
    quickFilters,
    sort,
    columns,
    chartSettings,
    relatedSignalEventsFilter,
    isAdvancedEditor,
    associatedVehiclesState,
  }: FilterSortState,
  {
    filtersKey,
    quickFiltersKey,
    sortKey,
    columnsKey,
    chartSettingsKey,
    relatedSignalEventsFilterKey,
    isAdvancedEditorKey,
    associatedVehiclesStateKey,
  }: QueryKeys
): FilterSortState => {
  const filtersQuery = getFiltersQuery(filters);
  const quickFiltersQuery = JSON.stringify(quickFilters);

  return {
    [filtersKey]: filtersQuery,
    [quickFiltersKey]: quickFiltersQuery,
    [isAdvancedEditorKey]: isAdvancedEditor ?? "",
    [sortKey]: sort || "",
    [columnsKey]: columns || "",
    [chartSettingsKey]: chartSettings ? JSON.stringify(chartSettings) : "",
    [relatedSignalEventsFilterKey]: relatedSignalEventsFilter
      ? JSON.stringify(relatedSignalEventsFilter)
      : "",
    [associatedVehiclesStateKey]: associatedVehiclesState
      ? JSON.stringify(associatedVehiclesState)
      : "",
  };
};

export const persistToLocalStorage = (
  filterSortState: FilterSortState,
  pageKeyWithVersion: string
) => {
  localStorage.setItem(
    `${TABLE_STATE_LOCAL_STORAGE_PREFIX}.${pageKeyWithVersion}`,
    JSON.stringify(filterSortState)
  );
};

/**
 * Modifies the URI params so they match our expected format and
 * merges given data with the current query params.
 */
export const getUpdatedQueryParams = (
  data: FilterSortState,
  queryKeys: UseInitialStateValuesAndKeysReturn["queryKeys"],
  currentQuery: qs.ParsedQs
) => {
  const updatedFiltersForQuery = modifyURIParams(data, queryKeys);

  const aggregatedParams = {
    ...currentQuery,
    ...updatedFiltersForQuery,
  };

  // Current query params + modified filters
  return qs.stringify(aggregatedParams, { arrayFormat: "indices" });
};

export const getFilterLabel = (label: string, filters: FilterGroupState) => {
  const filtersCount = getFilterStateCount(filters);

  return hasSomeFiltersAppliedFilterGroupState(filters)
    ? `${label} (${filtersCount})`
    : label;
};

/**
 * If the selected operator is not available for the current attribute due to disableXY props
 * we have to choose a new default one when switching the attribute.
 */
export const getFirstAvailableSelectedOperator = (
  t: TFunction,
  selected: FilterOperator | null,
  currentAttributeType: FilterType,
  attributeSchema?: SchemaEntry<string>
): FilterOperator => {
  const newDefaultOperator = getDefaultOperatorSelectOption(t, {
    type: currentAttributeType,
    ...attributeSchema?.filter,
  }).id;

  const availableOperators = getOperatorsSelectOptions(t, {
    type: currentAttributeType,
    ...attributeSchema?.filter,
  })
    .flat()
    .map(({ id }) => id);

  return selected && availableOperators.includes(selected)
    ? selected
    : newDefaultOperator;
};

interface GetNewFilterSortStateParams {
  disableUsingQuery: boolean;
  next: FilterSortState;
  currentQuery: ParsedQs;
  queryKeys: QueryKeys;
}

export const getNewFilterSortState = ({
  disableUsingQuery,
  next,
  currentQuery,
  queryKeys: {
    sortKey,
    filtersKey,
    quickFiltersKey,
    columnsKey,
    chartSettingsKey,
    relatedSignalEventsFilterKey,
    isAdvancedEditorKey,
    associatedVehiclesStateKey,
  },
}: GetNewFilterSortStateParams): FilterSortState => {
  if (disableUsingQuery) {
    return { ...next };
  }

  const queryChartSettings = getChartSettingsFromQuery(
    currentQuery,
    chartSettingsKey
  );

  // next.chartSettings potentially contains updated settings for one chart.
  // Merge it with the rest of the chart settings for the page.
  // We get the complete prior chart settings from the query string params.
  const chartSettings = injectPageChartSettings(
    queryChartSettings,
    next.chartSettings
  );

  const currentQueryFilters =
    currentQuery[filtersKey] !== undefined
      ? currentQuery[filtersKey].toString()
      : undefined;

  const currentQueryQuickFilters =
    currentQuery[quickFiltersKey] !== undefined
      ? currentQuery[quickFiltersKey].toString()
      : undefined;

  const currentQueryIsAdvancedEditor = currentQuery[isAdvancedEditorKey];

  return {
    sort: next.sort || (currentQuery[sortKey] as SortBy),
    filters:
      next.filters ||
      filterBuilderQueryToFilterBuilderState(currentQueryFilters),
    quickFilters: next.quickFilters
      ? next.quickFilters
      : currentQueryQuickFilters
        ? JSON.parse(currentQueryQuickFilters ?? "[]")
        : undefined,
    columns: next.columns || (currentQuery[columnsKey] as string[]),
    chartSettings,
    relatedSignalEventsFilter:
      next.relatedSignalEventsFilter ||
      getRelatedSignalEventsFilterFromQuery(
        currentQuery,
        relatedSignalEventsFilterKey
      ),
    isAdvancedEditor:
      next.isAdvancedEditor !== undefined
        ? next.isAdvancedEditor
        : currentQueryIsAdvancedEditor === "true",
    associatedVehiclesState:
      next.associatedVehiclesState ||
      getAssociatedVehiclesStateFromQuery(
        currentQuery,
        associatedVehiclesStateKey
      ),
  };
};

export const sanitizeChartSettings = (
  chartSettings: PageChartSettingsState
): PageChartSettingsState => {
  const result = cloneObject(chartSettings);

  Object.keys(result).forEach((tabKey) => {
    const tabSettings = result[tabKey];

    Object.keys(tabSettings).forEach((chartKey) => {
      const chartSettings = tabSettings[chartKey];

      chartSettings.forEach((setting, ix) => {
        if (setting.optionId === undefined || setting.optionId === null) {
          chartSettings.splice(ix, 1);
        }
      });
    });
  });

  return result;
};

export const getOperatorFromAPIOperator = (
  apiOperator: APIFilterOp
): FilterOperator => {
  const lowercaseOperator = apiOperator.toLowerCase() as APIFilterOp;

  return API_FILTER_TO_OPERATOR[lowercaseOperator];
};

export const isValidDate = (date: Date | null): boolean => {
  if (!date) return false;

  return isValid(date) && !isNaN(date.getTime()) && date.getFullYear() > 1000;
};

export const isFilterRowComplete = (f: FilterRowState) =>
  Boolean(
    f.attribute &&
    f.operator &&
    f.values &&
    areFilterValuesComplete(f.operator, f.values)
  );

export const filtersToQuickFilters = (filters: FilterGroupState) =>
  filters.children.filter((f) => f.type === "row") as FilterRowState[];

export const quickFiltersToFilters = (quickFilters: FilterRowState[]) => {
  const quickFiltersGroup: FilterGroupState = {
    type: "group",
    id: "group-quick-filters",
    anyAll: "all",
    children: quickFilters.filter((f) => isFilterRowComplete(f)),
  };

  return quickFiltersGroup;
};

export const quickFiltersToFiltersIncomplete = (
  quickFilters: FilterRowState[]
) => {
  const quickFiltersGroup: FilterGroupState = {
    type: "group",
    id: "group-quick-filters",
    anyAll: "all",
    children: quickFilters,
  };

  return quickFiltersGroup;
};

export const getStaticFilterOperator = (
  schema: SchemaEntry
): FilterOperator => {
  if (schema.accessor === "search") {
    return FilterOperator.CONTAINS;
  } else if (
    schema.filter?.filterType === "string" ||
    schema.filter?.filterType === "number"
  ) {
    return FilterOperator.IN;
  } else if (schema.filter?.filterType === "date") {
    return FilterOperator.IN_LAST;
  } else if (schema.filter?.filterType === "duration") {
    return FilterOperator.IN_LAST;
  } else if (schema.filter?.filterType === "exists") {
    return FilterOperator.EXISTS;
  } else {
    return FilterOperator.NOT_FILTERED;
  }
};

export const getStaticFilterLabel = (
  t: TFunction,
  schema: SchemaEntry
): string => {
  if (schema.accessor === "search") {
    return t("keyword_search");
  }

  return schema.label;
};
